import { Grid, GridItem, TextLink, Typography } from "@drone-js/core";
import { HStack, VStack } from "apps/commons/components/Stack";
import { useKeyDownHandler } from "apps/commons/hooks/useKeyDownHandler";
import type { PropertyData } from "libs/codegen/property-onboarding";
import { useEffect } from "react";
import { useWatch } from "react-hook-form";
import styled from "styled-components";
import { useStaticDataAtomValue } from "../staticDataAtom";
import { useExpandManualLocationAtomState } from "./expandManualLocationAtom";
import { CityDropdown } from "./manual/CityDropdown";
import { CountryDropdown } from "./manual/CountryDropdown";
import { FloorAndUnitInput } from "./manual/FloorAndUnitInput";
import { PostalCodeInput } from "./manual/PostalCodeInput";
import { StateDropdown } from "./manual/StateDropdown";
import { StreetAddressInput } from "./manual/StreetAddressInput";
import { useStateCityHandler } from "./useStateCityHandler";

const AlignedGrid = styled(Grid)`
  align-items: start;
`;

export const ManualLocationController = () => {
    const {
        cmsData: {
            location: { cantFindTheExactLocation, enterItManually, propertyLocation },
        },
    } = useStaticDataAtomValue();
    const { error } = useStateCityHandler();

    const stateId = useWatch<PropertyData>({
        name: "state.id",
    }) as number;
    const cityId = useWatch<PropertyData>({
        name: "city.id",
    }) as number;
    const streetAddress = useWatch<PropertyData>({
        name: "streetAddress",
    }) as string;

    const [expand, setExpand] = useExpandManualLocationAtomState();

    useKeyDownHandler(
        (e) => {
            if (e.key === "Enter" && expand === false) setExpand(true);
        },
        [setExpand],
    );

    useEffect(() => {
        // determine if location fields should be initially expanded
        setExpand(stateId !== 0 || cityId !== 0 || streetAddress !== "");
    }, []);

    if (!expand)
        return (
            <HStack gap="8">
                <Typography variant="body-s" textColor="base.subtle">
                    {cantFindTheExactLocation}
                </Typography>
                <TextLink
                    label={enterItManually}
                    onClick={() => setExpand(true)}
                    data-element-name="location-enter-manually"
                />
            </HStack>
        );

    return (
        <VStack gap="16">
            <Typography variant="body-default-strong">{propertyLocation}</Typography>
            <CountryDropdown error={error} />
            <AlignedGrid>
                <GridItem md={6} sm={12}>
                    <StateDropdown />
                </GridItem>
                <GridItem md={6} sm={12}>
                    <CityDropdown />
                </GridItem>
            </AlignedGrid>
            <StreetAddressInput />
            <FloorAndUnitInput />
            <PostalCodeInput />
        </VStack>
    );
};
